import type { AssignmentPlan, BreakoutStage } from "./types.js";

export interface LiveBreakoutRoomOccupant {
  participantUUID: string;
  displayName?: string;
  /** "assigned" is an allocation Zoom has not moved anyone into yet. */
  participantStatus: "assigned" | "joined";
}

export interface LiveBreakoutRooms {
  rooms: { breakoutRoomId: string; name: string; participants?: LiveBreakoutRoomOccupant[] }[];
  state: "open" | "closed";
}

/**
 * Who Zoom reports as actually sitting in each room, keyed by room name.
 *
 * This is the only occupancy source that cannot go stale. The stored
 * room_assignments belong to one assignment run, so any refresh the server
 * answers with a different run reports nobody seated while two people are
 * plainly in the room — the note went stale, not the room.
 *
 * A room is absent from the map when this client cannot see inside it. Zoom
 * returns the `participants` array to the meeting owner only; a co-host's list
 * carries room names and nothing else. So a missing entry means "cannot see",
 * never "empty" — conflating the two would report every room as deserted to
 * every co-host.
 */
export function occupantsByRoom(live: LiveBreakoutRooms | null): Map<string, LiveBreakoutRoomOccupant[]> {
  const byRoom = new Map<string, LiveBreakoutRoomOccupant[]>();
  for (const room of live?.rooms ?? []) {
    if (!room.participants) continue;
    byRoom.set(
      room.name,
      room.participants.filter((p) => p.participantStatus === "joined"),
    );
  }
  return byRoom;
}

export interface ResumePoint {
  stage: BreakoutStage;
  roomByName: Map<string, string>;
}

/**
 * Decide where a newly opened app should land, given what the server recorded
 * and what the meeting actually contains. The meeting wins (AC-4).
 */
export function resolveResumePoint(
  plan: AssignmentPlan,
  live: LiveBreakoutRooms | null,
): ResumePoint {
  const planned = new Set(plan.rooms.map((room) => room.room_name));
  const matching = (live?.rooms ?? []).filter((room) => planned.has(room.name));
  if (matching.length === 0) {
    return { stage: "idle", roomByName: new Map() };
  }

  const roomByName = new Map(matching.map((room) => [room.name, room.breakoutRoomId]));
  const recorded = plan.stage ?? "idle";
  // "idle" means nobody has driven *this* run yet, so it holds no placements —
  // open rooms carrying its names came from another round. Room matching is by
  // name, and a CSV that labels every round "Breakout Room 1" makes that
  // collision routine, so trusting live.state here promoted a fresh run
  // straight to "Live · people inside" over a plan with zero seated
  // participants. Landing on "created" states what the run actually holds.
  if (recorded === "idle") return { stage: "created", roomByName };
  if (live?.state === "open") return { stage: "opened", roomByName };
  if (recorded === "opened") return { stage: "closed", roomByName };
  return { stage: recorded, roomByName };
}

/**
 * Every room UUID a round's cleanup should delete, from both sources that know
 * about them: this client's own cache and the meeting's live list.
 *
 * The cache is keyed by room *name*, so it holds at most one UUID per name —
 * when two operators each ran Create, Zoom holds two rooms per name and the
 * cache can only name one of them. Reading the live list as well (narrowed to
 * planned names so a room made by hand in Zoom is left alone) picks up the
 * duplicates, and the union is deduped by UUID so a room known to both sources
 * is not deleted twice.
 */
export function cleanupTargetUuids(
  cachedUuids: Iterable<string>,
  live: LiveBreakoutRooms | null,
  plannedNames: Iterable<string>,
): string[] {
  const planned = new Set(plannedNames);
  const targets = new Set(cachedUuids);
  if (planned.size > 0) {
    for (const room of live?.rooms ?? []) {
      if (planned.has(room.name)) targets.add(room.breakoutRoomId);
    }
  }
  return Array.from(targets);
}

const STAGE_DONE_MESSAGE: Record<BreakoutStage, string> = {
  idle: "",
  created: "The team has already created the breakout rooms.",
  assigned: "The team has already assigned participants.",
  opened: "The team has already opened the rooms.",
  closed: "The team has already closed the rooms.",
  failed: "",
};

/**
 * Confirmation copy for AC-5: names what the team already did before asking
 * whether the operator still wants to redo the step.
 */
export function stageMovedMessage(current: BreakoutStage, action: string): string {
  // Phrased as a statement, not a question: the app cannot ask one. Zoom's
  // webview blocks window.confirm, so this is shown as a banner after the app
  // has already re-synced to the shared stage.
  return `${STAGE_DONE_MESSAGE[current]} The round moved on, so "${action}" was not run — check the step above and try again.`;
}

/**
 * Copy for when the shared round is ahead of what this client can actually see.
 *
 * resolveResumePoint() trusts the meeting (AC-4), so an operator whose Zoom
 * client has not caught up — or has genuinely lost sight of the rooms, which
 * happens — re-syncs to a step *behind* the one the team recorded. Telling them
 * to "check the step above" there is a loop: the step above is the one they just
 * pressed. Name the disagreement instead.
 */
export function stageOutOfViewMessage(shared: BreakoutStage, action: string): string {
  return `${STAGE_DONE_MESSAGE[shared]} This Zoom client cannot see those rooms, so "${action}" was not run — press Refresh, and rejoin the meeting if the step still looks wrong.`;
}
